import { cn, formatNumber } from '@worldcoo/ui/lib/utils';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { EmptyChar } from './empty-char';
import { useTranslation } from '@/lib/i18n';
import { useLingui } from '@lingui/react/macro';
import { usePortalCampaignQuery, usePortalMetadataQuery, usePortalCampaignAggregateQuery } from '@/lib/data-hooks';
import { PortalAnimateNumber } from './portal-animate-number';
import { useClient } from '@worldcoo/ui/hooks/use-client';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { isNotFound } from '@tanstack/react-router';
import { RingProgressBar } from './ring-progress-bar';

export function CampaignStatistics(props: {
	campaignId: string;
	textEffectDisabled?: boolean;
	variant?: 'sm';
	/** Below `lg` only, where the sidebar progress card is hidden. */
	showFunded?: boolean;
}) {
	const { t } = useLingui();
	const metadata = usePortalMetadataQuery();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	// Once, not per counter: a client-rendered streamed boundary leaves a stale `<div hidden id="S:n">` copy,
	// duplicating every counter's `data-testid`.
	const campaign = usePortalCampaignQuery(props.campaignId);

	if (isNotFound(metadata) || isNotFound(campaign)) return null;
	if (!aggregates || !('aggregates' in aggregates)) {
		return null;
	}

	if (metadata.hideStatsOnActiveCampaigns && !aggregates.aggregates[props.campaignId]?.finished) {
		return null;
	}

	return (
		<>
			{/* The whole block, not just the figures (TEC-3703): docs/notes/portals/statistics-campaign.md */}
			<div
				translate="no"
				className={cn(
					'bg-background flex gap-6 sm:gap-10 lg:gap-16 justify-between lg:justify-start',
					metadata.hideCollected && 'justify-center',
					props.variant === 'sm' && 'gap-16',
				)}
			>
				<div className="flex flex-col font-medium">
					<DonationsIcon className={cn('text-primary mb-2', props.variant === 'sm' && 'size-6')} />
					<div className={cn('whitespace-nowrap text-xl xl:text-2xl', props.variant === 'sm' && 'text-xl xl:text-xl')}>
						<CampaignDonations
							campaignId={props.campaignId}
							textEffectDisabled={props.textEffectDisabled}
							isPageLoaded={campaign.isPageLoaded}
						/>
					</div>
					<span
						className={cn(
							'text-primary text-nowrap text-sm font-semibold lg:text-base',
							props.variant === 'sm' && 'text-sm lg:text-sm',
						)}
					>
						{t`Donations`}
					</span>
				</div>

				{!metadata.hideCollected && (
					<div className="flex flex-col font-medium">
						<CollectedIcon className={cn('text-primary mb-2', props.variant === 'sm' && 'size-6')} />
						<div
							className={cn('whitespace-nowrap text-xl xl:text-2xl', props.variant === 'sm' && 'text-xl xl:text-xl')}
						>
							<CampaignCollected
								campaignId={props.campaignId}
								textEffectDisabled={props.textEffectDisabled}
								isPageLoaded={campaign.isPageLoaded}
							/>
						</div>
						<div
							className={cn(
								// Wraps below `lg`: "Objetivo de 1.500.000 €" plus the funded column overflows the viewport.
								'text-primary text-sm font-semibold lg:text-nowrap lg:text-base',
								props.variant === 'sm' && 'text-nowrap text-sm lg:text-sm',
							)}
						>
							<CampaignCollectedFromBudget campaignId={props.campaignId} />
						</div>
					</div>
				)}

				{props.showFunded && <CampaignFunded campaignId={props.campaignId} isPageLoaded={campaign.isPageLoaded} />}
			</div>
		</>
	);
}

function CampaignFunded(props: { campaignId: string; isPageLoaded: boolean }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);

	const campaignAggregate =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId] : undefined;
	// The real figure: `AnimateNumber` owns the first-paint guard.
	const progress = useDebounce(campaignAggregate?.funding ? campaignAggregate.progress : 0, 150);

	// Truthy, not `== null`: 0 funding is "no goal" too, and its 0% ring beside "Collected" was TEC-3639.
	if (!campaignAggregate?.funding) return null;

	// Ignores `textEffectDisabled`: the ring has no such opt-out, so honouring it would desync the pair.
	const instant = props.isPageLoaded;

	return (
		<div className="flex flex-col font-medium lg:hidden">
			<RingProgressBar
				radius={40}
				stroke={6}
				className="mb-2 size-8"
				inset={false}
				progress={Math.min(progress, 100)}
				strokeColor="var(--primary)"
				color="var(--primary)"
				entrance={!instant}
			/>
			<div className="text-xl whitespace-nowrap">
				<PortalAnimateNumber locale={language} value={progress} suffix="%" instant={instant} />
			</div>
			<span className="text-primary text-sm font-semibold text-nowrap">{t`Funded`}</span>
		</div>
	);
}

function CampaignDonations(props: { campaignId: string; textEffectDisabled?: boolean; isPageLoaded: boolean }) {
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const isClient = useClient();

	const donors = aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId]?.donors : undefined;

	// An aggregate entry that never arrives is not zero donations.
	if (isClient && donors == null) return <EmptyChar />;

	return (
		<PortalAnimateNumber
			testId="donations"
			locale={language}
			value={donors ?? 0}
			instant={props.textEffectDisabled || props.isPageLoaded}
		/>
	);
}

function CampaignCollected(props: { campaignId: string; textEffectDisabled?: boolean; isPageLoaded: boolean }) {
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const isClient = useClient();

	const collected =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId]?.collected : undefined;

	// See CampaignDonations: a missing aggregate entry is not zero collected.
	if (isClient && collected == null) return <EmptyChar />;

	return (
		<PortalAnimateNumber
			testId="collected"
			locale={language}
			value={collected ?? 0}
			suffix="€"
			decimalDigits={2}
			instant={props.textEffectDisabled || props.isPageLoaded}
		/>
	);
}

function CampaignCollectedFromBudget(props: { campaignId: string }) {
	const { t } = useLingui();
	const { language } = useTranslation();
	const aggregates = usePortalCampaignAggregateQuery(props.campaignId);
	const funding =
		aggregates && 'aggregates' in aggregates ? aggregates.aggregates[props.campaignId]?.funding : undefined;

	// Not a bare string, so a translator's `<font>` nests in a node React owns (TEC-3703).
	if (!funding) return <span>{t`Collected`}</span>;
	const goal = formatNumber(language, funding, 0);
	return <span>{t`Goal of ${goal}` + ' €'}</span>;
}
